import { models, stores } from '@kurtosys/app-start';
import { models as eventManagerModels } from '@kurtosys/event-manager';
import { common, query } from '@kurtosys/ksys-app-template';
import { IListenForOptions } from '@kurtosys/types/app/IListenForOptions.js';
import { TRender } from '@kurtosys/types/app/TRender.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IAppInitialisedDetail, ISourceApplication } from '@kurtosys/types/eventBus/index.js';
import { IRedirect } from '@kurtosys/types/helpers/redirect/IRedirect.js';
import { action, computed, makeObservable, observable, override, toJS } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData/index.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { Feature } from '../../shared/Feature.js';
import { IAppTabGrid } from '../../Tabs/models/IAppTabGrid.js';
import { TabsStore } from '../../Tabs/stores/TabsStore.js';
import { IAppComponents } from '../models/IAppComponents.js';
import { IConditionalConfig } from '../models/IConditionalConfig.js';

/* [Component: appStoreComponentImport] */
type EventBusStore = stores.EventBusStore<IConfiguration, IComponentStyles>;
type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

interface IAppEmbeds {
	sourceApplication: ISourceApplication;
	eventDetail?: IAppInitialisedDetail;
}

export type HasDataMap = Map<string, boolean | 'loading'>;

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	static EVENT_BUS_KEY: eventManagerModels.EventTypes = eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE;
	@observable.ref appEmbeds: IAppEmbeds[] = [];
	@observable appHasDataMap: HasDataMap = new Map();
	url: URL;
	appLevelGuid: string;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		this.url = new URL(url);
		this.appLevelGuid = common.commonUtils.generateGuid();
		makeObservable(this);
	}

	@computed
	get hasData(): boolean {
		const tabsStore = this.storeContext.get<TabsStore>('tabsStore');
		const { tabsProps } = tabsStore;

		return (tabsProps && tabsProps.tabComponents && tabsProps.tabComponents.length > 0) || false;
	}

	@computed
	get appHasData(): HasDataMap {
		return toJS(this.appHasDataMap);
	}

	@computed
	get displayMode(): string | undefined {
		return this.appComponentConfiguration.features?.displayMode || 'tabs';
	}

	isConditionValid = (conditional: IConditionalConfig | undefined): boolean => {
		if (conditional === undefined || conditional.conditions === undefined) {
			return true;
		}
		const { inputs } = this.appParamsHelper.values;

		const conditionalHelper = new common.helpers.ConditionalHelper({ queryClass: query.Query }, conditional);
		const result = conditionalHelper.matchesWithOptions({
			instance: inputs,
			executionOptions: this.queryStore.executionOptions,
		});
		return result;
	};

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	/**
	 * Overriding standard configuration for listenFor event options.
	 *
	 * @readonly
	 */
	@override
	get listenFor(): IListenForOptions[] {
		// pull in static config
		const base = (this.appComponentConfiguration && this.appComponentConfiguration.listenFor) || [];

		// generate listenFor options based on application embeds within the grid.
		const gridEvents = this.appEmbeds.map((embed) => {
			return {
				appId: embed.sourceApplication.applicationId || embed.sourceApplication.templateId,
				eventTypes: [
					{
						name: eventManagerModels.EventTypes.APP_INITIALIZED,
						requiredForInitialLoad: false,
						runOnce: true,
						cache: {
							enabled: false,
						},
					},
				],
			};
		});

		// combine base & grid event options.
		const rawListenForOptions = [...base, ...gridEvents];

		// Create unique IListenForOptions, merging dynamic options with static config options.
		const apps: Map<string, IListenForOptions> = new Map();
		rawListenForOptions.forEach((app) => {
			const existing = apps.get(app.appId);
			if (!existing) {
				apps.set(app.appId, app);
			} else {
				app.eventTypes.forEach((appEventType) => {
					const existingEventTypeIndex = existing.eventTypes.findIndex(
						(existingEventType) => existingEventType.name === appEventType.name,
					);
					if (existingEventTypeIndex < 0) {
						existing.eventTypes.push(appEventType);
					} else {
						// due to grid event options being appended after base in rawListenForOptions, the below implies it will override matching static configs.
						existing.eventTypes[existingEventTypeIndex] = {
							...existing.eventTypes[existingEventTypeIndex],
							...appEventType,
						};
					}
				});
			}
		});

		// convert Map into IListenForOptions Array
		const workingListenForOptions: IListenForOptions[] = [];
		for (const key of apps.keys()) {
			const options = apps.get(key);
			if (options) {
				workingListenForOptions.push(options);
			}
		}

		return workingListenForOptions;
	}

	@action
	setAppHasDataItem = ({ sourceApplication, hasData }: IAppInitialisedDetail) => {
		this.appHasDataMap.set(this.hasDataMapKey(sourceApplication), hasData);
	};

	hasDataMapKey = (componentProps: ISourceApplication) => {
		return `${componentProps.templateId}-${componentProps.applicationCode}-${componentProps.configurationKey}`;
	};

	@action
	addApplicationEmbed = (componentProps: ISourceApplication) => {
		if (!componentProps.applicationId) {
			componentProps.applicationId = componentProps.templateId;
		}
		if (!componentProps.configurationKey) {
			componentProps.configurationKey = 'default';
		}
		if (!componentProps.styleKey) {
			componentProps.styleKey = 'default';
		}
		if (!componentProps.themeKey) {
			componentProps.themeKey = 'default';
		}

		this.appHasDataMap.set(this.hasDataMapKey(componentProps), 'loading');
		this.appEmbeds = [...this.appEmbeds, { sourceApplication: componentProps }];
	};

	/**
	 * Updates the grid component reference with the app init event details
	 */
	@action
	updateApplicationEmbedAtIndex = (embedIdx: number, eventDetail: IAppInitialisedDetail) => {
		const targetEmbed = this.appEmbeds[embedIdx];
		if (embedIdx < 0) {
			// invalid embed index
			return false;
		}
		if (!targetEmbed) {
			// embed doesn't exist
			return false;
		}
		if (targetEmbed.eventDetail) {
			// embed has already initialized.
			return true;
		}
		// otherwise update
		const appEmbedsClone = [...this.appEmbeds];
		const updatedEmbed = {
			...appEmbedsClone[embedIdx],
			eventDetail: { ...eventDetail },
		};
		appEmbedsClone[embedIdx] = updatedEmbed;
		this.appEmbeds = appEmbedsClone;
		return true;
	};

	appEmbedFilterBySourceKeyCallback(targetSourceAppKey: string) {
		return (embed: IAppEmbeds) => {
			let key;
			if (embed.sourceApplication) {
				key = this.eventBusStore.generateSourceAppKey(embed.sourceApplication);
			}
			return key === targetSourceAppKey;
		};
	}

	getAppEmbedIdxForSourceApplication(targetSourceKey: string): number {
		return this.appEmbeds.findIndex(this.appEmbedFilterBySourceKeyCallback(targetSourceKey));
	}

	getAppEmbedForSourceApplication(targetSourceKey: string) {
		return this.appEmbeds.find(this.appEmbedFilterBySourceKeyCallback(targetSourceKey));
	}

	gridEmbeds(grid: IAppTabGrid[]) {
		for (const tabGridItem of grid) {
			if (tabGridItem.applicationEmbed) {
				const componentProps = tabGridItem?.applicationEmbed;
				const componentEmbed = this.orchestrateEmbed(componentProps, 'ksys-app-other') as ISourceApplication;
				this.addApplicationEmbed(componentEmbed);
			}
			if (tabGridItem.grid) {
				this.gridEmbeds(tabGridItem.grid);
			}
		}
	}

	setAppEmbeds = () => {
		const tabsStore = this.storeContext.get('tabsStore') as TabsStore;
		if (tabsStore && tabsStore.configuration) {
			const { children: tabs } = tabsStore.configuration;
			for (const tabItem of tabs) {
				this.gridEmbeds(tabItem.grid);
			}
		}
	};

	async customInitializeAfter() {
		const tabsStore = this.storeContext.get('tabsStore');
		tabsStore.initialize(null);
	}

	async customInitializeBefore() {
		this.setAppEmbeds();
		// TODO: update this to work like the grid (if hide empty apps, then trigger child event status)
		this.eventBusStore.initialize({
			[eventManagerModels.EventTypes.APP_INITIALIZED]: async (baseEvent: eventManagerModels.EventPublish<eventManagerModels.detail.EventAppInitialisedDetail>) => {
				const eventDetail = { ...baseEvent.detail };
				const targetSourceKey = this.eventBusStore.getSourceAppKeyFromEvent(baseEvent);
				const currentAppEmbedIdx = this.getAppEmbedIdxForSourceApplication(targetSourceKey);
				this.updateApplicationEmbedAtIndex(currentAppEmbedIdx, eventDetail);
				this.setAppHasDataItem(eventDetail);
				// if (this.appEmbeds.every((embed) => embed.eventDetail)) {
				// 	// Add logic here that you want to run when
				// }
			},
		});
	}

	@computed
	get initialActiveTab(): string | undefined {
		const activeTabQuery =
			this.appComponentConfiguration &&
			this.appComponentConfiguration.activeTabManagement &&
			this.appComponentConfiguration.activeTabManagement.initialValue;
		if (activeTabQuery) {
			return this.getQueryValue(activeTabQuery);
		}
	}

	@computed
	get urlRewrite(): IRedirect | undefined {
		return (
			this.appComponentConfiguration &&
			this.appComponentConfiguration.activeTabManagement &&
			this.appComponentConfiguration.activeTabManagement.urlRewrite
		);
	}

	@computed
	get intersectionRender(): TRender {
		return this.appParamsHelper?.rawAppParams?.render;
	}
}
